import { useEffect, useState } from "react";
import { DESIGN_DIRECTION_FONT_URLS } from "./designFonts.generated";

/**
 * Design System v2 — design-direction switching (EXPERIMENT).
 *
 * A design direction is a complete token set (colour, type, shape, elevation) defined in
 * `themes.css` and activated with `<html data-ds="...">`. With no direction set, the legacy
 * tokens apply and the UI looks exactly as before.
 *
 * Activation (dev / review only):
 *   - URL:      ?ds=ledger | mandi | graphite | navy   (persists to localStorage)
 *   - URL:      ?ds=off                                (clears)
 *   - Storage:  localStorage["mdd_ds"]
 */
export type DesignDirection = "ledger" | "mandi" | "graphite" | "navy";

export interface DesignDirectionMeta {
  id: DesignDirection;
  letter: string;
  label: string;
  tagline: string;
}

export const DESIGN_DIRECTIONS: DesignDirectionMeta[] = [
  { id: "ledger", letter: "A", label: "Ledger", tagline: "Swiss / editorial · ink-black actions · hairline borders" },
  { id: "mandi", letter: "B", label: "Mandi Modern", tagline: "Warm commerce · forest green · amber offers" },
  { id: "graphite", letter: "C", label: "Graphite", tagline: "Operator console · graphite chrome · lime CTAs" },
  { id: "navy", letter: "D", label: "Trust Navy", tagline: "Fintech trust · navy chrome · saffron CTAs" },
];

const STORAGE_KEY = "mdd_ds";
const FONT_LINK_ID = "mdd-ds-font";

function isDirection(v: string | null | undefined): v is DesignDirection {
  return DESIGN_DIRECTIONS.some((d) => d.id === v);
}

/** Resolve the active direction from `?ds=` (persisting it) or localStorage. */
export function readDesignDirection(): DesignDirection | null {
  if (typeof window === "undefined") return null;
  try {
    const param = new URLSearchParams(window.location.search).get("ds");
    if (param === "off") {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (isDirection(param)) {
      localStorage.setItem(STORAGE_KEY, param);
      return param;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    return isDirection(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Apply a direction to <html> and lazy-load its web fonts. Pass null to restore legacy. */
export function applyDesignDirection(ds: DesignDirection | null): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const existing = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;

  if (!ds) {
    delete root.dataset.ds;
    existing?.remove();
    return;
  }

  root.dataset.ds = ds;
  const href = DESIGN_DIRECTION_FONT_URLS[ds];
  if (!href) return;
  if (existing) {
    if (existing.href !== href) existing.href = href;
    return;
  }
  const link = document.createElement("link");
  link.id = FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

/** React hook: resolves + applies the active direction once on mount. */
export function useDesignDirection(): DesignDirection | null {
  const [ds] = useState<DesignDirection | null>(() => readDesignDirection());
  useEffect(() => {
    applyDesignDirection(ds);
  }, [ds]);
  return ds;
}
